import { useQuery } from '@apollo/client';
import { TableProps } from 'antd';
import { SorterResult } from 'antd/lib/table/interface';
import { createSortDescription } from 'Components/Table/createColumns';
import { useCallback, useEffect, useState } from 'react';
import { GET_SYSTEM_ROLES } from 'Services/Api/Roles/Queries';
import {
	GetSystemRolesRequest,
	GetSystemRolesResponse,
	SystemRole
} from 'Services/Api/Roles/Types';
import useColumns from './columns';
import { PermissionSectionData, RoleSubmitPayload } from './components/PermissionList';
import { createPrimaryRoleOverride, createTenantRole, getRoleInfo, getTenantRolePermissionList, getTenantRoles, updateTenantRole } from './service/index.Service';
import { mapApiPermissionsToSections } from './utils/Roles.utils';
import { systemsStore } from 'Stores/SystemsStore';
import { Notification } from 'Components';
import { getAppRoutes } from 'Pages/App/App.constants';
import { useHistory } from 'react-router';
import { useTranslation } from 'react-i18next';
export interface RolePermission {
	id: number;
	code: string;
	moduleId: number | null;
}

export interface RoleDetail {
	id: number;
	role: string;
	createdDate: string;
	isPrimary: boolean;
	twoFactorAuthorizationEnabled: boolean;
	description?: string;
	permissions: RolePermission[];
}
export function usePermissionList() {
	const [permissionList, setPermissionList] = useState<PermissionSectionData[]>([]);
	const [loading, setLoading] = useState<boolean>(true);
	const { tenantList, selectedTenant } = systemsStore;
	const selectedTenantId: number | string = selectedTenant?.value ?? tenantList[0]?.value;
	useEffect(() => {
		const fetchPermissions = async () => {
			if (selectedTenantId === undefined) {
				setPermissionList([]);
				return;
			}

			setLoading(true);
			try {
				const tenantId = Number(selectedTenantId) === 0
					? 0
					: Number(selectedTenantId);
				const response = await getTenantRolePermissionList(tenantId);
				setPermissionList(
					mapApiPermissionsToSections(response)
				);
			} finally {
				setLoading(false);
			}
		};
		fetchPermissions();
	}, [selectedTenantId]);

	return {
		permissionList,
		loading,
	};
}

export function useRoleDetail(roleCode: string) {
	const { permissionList, loading: permissionsLoading } = usePermissionList();
	const { tenantList, selectedTenant } = systemsStore;
	const tenantId =
		Number(selectedTenant?.value ?? tenantList[0]?.value) === 0
			? 0
			: Number(selectedTenant?.value ?? tenantList[0]?.value);
	const [role, setRole] = useState<RoleDetail | undefined>();
	const [loading, setLoading] = useState(false);
	const history = useHistory();
	const fetchRole = useCallback(async () => {
		if (!roleCode) {
			setRole(undefined);
			return;
		}
		setLoading(true);
		try {
			const response = await getRoleInfo(
				tenantId,
				roleCode,
			);
			setRole(response);
		} catch (err) {
			history.push(getAppRoutes().ROLES);
		} finally {
			setLoading(false);
		}
	}, [tenantId, roleCode]);

	useEffect(() => {
		fetchRole();
	}, [fetchRole]);

	return {
		role,
		permissionList,
		loading: loading || permissionsLoading,
		refetch: fetchRole,
	};
}

export function useCreateRole() {
	const [loading, setLoading] = useState(false);
	const tenantId = Number(systemsStore.selectedTenant?.value ?? 0);

	const createRole = async (values: RoleSubmitPayload) => {
		setLoading(true);
		try {
			const role = await createTenantRole({
				tenantId,
				roleName: values.roleName,
				description: values.description,
				permissionIds: values.permissions.map(Number),
			});
			Notification.success({ description: role.message });
		} finally {
			setLoading(false);
		}
	};

	return [createRole, { loading }] as const;
}

export function useUpdateRole() {
	const [loading, setLoading] = useState(false);
	const tenantId = Number(systemsStore.selectedTenant?.value ?? 0);
	const { t } = useTranslation();

	const updateRole = async (selectedRole: any, values: RoleSubmitPayload) => {
		setLoading(true);
		try {
			if (!!selectedRole?.isPrimary) {
				const role = await createPrimaryRoleOverride({
					tenantId,
					systemRoleTemplateId: selectedRole.id,
					description: values.description,
					permissionIds: values.permissions.map(Number),
				});
				Notification.success({ description: t(role.message) });
			} else {
				const role = await updateTenantRole(Number(selectedRole.id), {
					tenantId,
					roleName: values.roleName,
					description: values.description,
					permissionIds: values.permissions.map(Number),
				});
				Notification.success({ description: t(role.message) });
			}
		} finally {
			setLoading(false);
		}
	};

	return [updateRole, { loading }] as const;
}

export const GET_ROLES_INITIAL_PARAMS: GetSystemRolesRequest = {
	sort: createSortDescription({
		columnKey: 'roleName',
		order: 'ascend'
	}),
	filters: {}
};

const mapTenantRoleResponse = (items: any[] = []): SystemRole[] =>
	items.map((item) => ({
		id: item.id,
		roleName: item.roleName,
		description: item.description,
		type: item.type,
		isPrimaryRoleOverride: item.isPrimaryRoleOverride,
		usersCount: Number(item.usersCount ?? 0),
		updatedDate: item.updatedDate
	}));

export const useRoles = () => {
	const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
	const [selectedTenantId, setSelectedTenantId] = useState<number | string>("");
	const [loading, setLoading] = useState<boolean>(false);
	const [dataSource, setDataSource] = useState<SystemRole[]>([]);
	const [params, setParams] = useState<GetSystemRolesRequest>(
		GET_ROLES_INITIAL_PARAMS
	);
	const [page, setPage] = useState(1);
	const [pageSize, setPageSize] = useState(10);
	const [roleName, setRoleName] = useState("");
	const [total, setTotal] = useState(0);
	const result = useQuery<GetSystemRolesResponse, GetSystemRolesRequest>(
		GET_SYSTEM_ROLES,
		{
			variables: GET_ROLES_INITIAL_PARAMS,
			notifyOnNetworkStatusChange: true,
			skip: !selectedTenantId
		}
	);

	const refetch = async (
		values: GetSystemRolesRequest = GET_ROLES_INITIAL_PARAMS, tenantId?: string | number,
		currentPage: number = page,
		currentPageSize: number = pageSize
	) => {
		setParams(values);

		const targetTenantId = tenantId ?? selectedTenantId;
		const hasTenant = targetTenantId !== undefined && targetTenantId !== '';
		
		if (hasTenant) {
			setLoading(true);
			try {
				const sort = values.sort || 'roleName:asc';
				const [sortBy, order] = sort.split(':');

				const response = await getTenantRoles(targetTenantId, {
					dropdown: false,
					allTenants: targetTenantId === 0,
					role: values?.filters?.roleName ?? '',
					sortBy: sortBy || 'roleName',
					sortOrder: order?.toUpperCase() === 'DESC' ? 'DESC' : 'ASC',
					page: currentPage,
					limit: currentPageSize
				});
				setDataSource(mapTenantRoleResponse(response?.data));
				setTotal(response.total);
				setPage(response.page);
				setPageSize(response.limit);
			} finally {
				setLoading(false);
			}
		}
	};

	const onTenantChange = async (tenantId: string | number) => {
		setSelectedTenantId(tenantId);
		setLoading(true);
		try {
			const values: GetSystemRolesRequest = GET_ROLES_INITIAL_PARAMS
			const variables: GetSystemRolesRequest = {
				...values,
				filters: {
					...values.filters,
				}
			};
			await refetch(variables, tenantId);
		} finally {
			setLoading(false);
		}
	};


	const onTableChange: TableProps<SystemRole>['onChange'] = (
		pagination,
		filters,
		sorter
	) => {
		const sort = createSortDescription<SystemRole>(
			sorter as SorterResult<SystemRole>
		);

		const variables: GetSystemRolesRequest = {
			sort,
			filters: {
				roleName:roleName
			}
		};

		refetch(
			variables,
			undefined,
			pagination.current ?? 1,
			pagination.pageSize ?? 10
		);
	};

	const columns = useColumns({
		params
	});

	const onPaginationChange = (
		page: number,
		pageSize?: number
	) => {
		refetch(
			params,
			undefined,
			page,
			pageSize ?? 10
		);
	};

	return {
		result,
		dataSource,
		columns,
		onTableChange,
		selectedRowKeys,
		setSelectedRowKeys,
		selectedTenantId,
		setSelectedTenantId,
		onTenantChange,
		refetch,
		loading,
		page,
		pageSize,
		total,
		params,
		onPaginationChange,
		roleName,
		setRoleName,
		setLoading
	};
};
